/**
 * finance 域 Zod 共享契约 — ADM-FIN-S001~S006
 *
 * 金额口径（dev-docs `02-API契约/A1-运营财务.md` + `03-数据字典/60-订阅.md` §8「ADM-FIN 财务统计聚合口径」，
 * 张培 #119 已 merge）：财务统计读 `purchase_amounts` **原币列**（`paid_minor_units` + `paid_currency`），
 * **多币种 MVP 不做汇率归一**、一律 `GROUP BY paid_currency` 分币种返回 → 各响应以 `by_currency: [{ currency, ... }]`
 * 组织，**非**单值（旧实现误 SUM `coin_transactions.amount`=Coin 数量，已废）。金额 bigint minor units → API string。
 */

import { z } from 'zod';

const LocaleSchema = z
  .string()
  .min(2)
  .max(16)
  .regex(/^[a-zA-Z]{2,3}(-[A-Za-z0-9]{2,8})*$/);

export const GrantBonusRequestSchema = z.object({
  user_id: z.string().uuid(),
  amount: z.string().regex(/^[1-9][0-9]*$/, 'amount must be positive integer string'),
  reason_i18n: z
    // 每个 locale 值都拒纯空白（不只 en）：否则 { en:'x', de:'   ' } 会持久化空白 de，
    // C 端按用户语言派生展示时德语用户拿到空白原因而非 fallback en（codex bot #217 P2）。
    .record(
      LocaleSchema,
      z
        .string()
        .min(1)
        .max(500)
        .refine((s) => s.trim().length > 0, { message: 'reason text must not be blank' }),
    )
    .refine((m) => typeof m.en === 'string' && m.en.trim().length > 0, {
      message: "reason_i18n must contain non-empty 'en'",
    }),
});
export type GrantBonusRequest = z.infer<typeof GrantBonusRequestSchema>;

export type GrantBonusResponse = {
  grant_id: string;
  user_id: string;
  amount: string;
  expires_at: string;
};

// ─────────────────────────── ADM-FIN-S004 配套 · BONUS 发放月度统计 ───────────────────────────

/**
 * BONUS 发放月度 KPI（ADM-FIN-S004 配套，平台级）。
 * 字段映射（聚合 bonus_grants WHERE source='ADMIN_GRANT' 按自然月 → API）：
 * - total_amount：Σ amount（BONUS 数量，**非货币** minor units；bigint → string，避免精度丢失）
 * - grant_count：发放笔数 = COUNT(*)（number；与「人次」对齐，每笔 grant 一次发放）
 * - avg_per_grant：total_amount / grant_count（四舍五入整数 string；count=0 时 '0'，防除零）
 *
 * 平台级：bonus_grants **无 app_id 列** → 不按 App 维度过滤（与 finance 其它 ?app_id 端点不同）。
 * current=本月（自然月 UTC）、previous=上一自然月，供前端算真实环比 delta（不硬编码假数字）。
 */
export type BonusStatsResponse = {
  current: { total_amount: string; grant_count: number; avg_per_grant: string };
  previous: { total_amount: string; grant_count: number; avg_per_grant: string };
};

export type FinanceStatsScope = 'all' | 'app';

// ─────────────────────────── ADM-FIN-S001 收入统计（原币分币种）───────────────────────────

/**
 * 单币种收入聚合行（ADM-FIN-S001，per `paid_currency`）。
 * 字段映射（聚合 purchase_amounts WHERE kind=PURCHASE GROUP BY paid_currency → API）：
 * - currency：原币 ISO 4217（`paid_currency`）
 * - charge_orders / charge_users：该币种 PURCHASE 行数 / 去重 user_id（**per-currency，跨币种不去重相加**）
 * - avg_charge_count_per_user：orders / users（2 位小数 string）
 * - total_revenue_minor_units：Σ paid_minor_units（该币种原币 minor units → string）
 * - avg_order_value_minor_units：total_revenue / orders（同币种内整除 minor units string）
 */
export type FinanceCurrencyIncomeRow = {
  currency: string;
  charge_orders: number;
  charge_users: number;
  avg_charge_count_per_user: string;
  total_revenue_minor_units: string;
  avg_order_value_minor_units: string;
};

export type FinanceIncomeStatsResponse = {
  scope: FinanceStatsScope;
  app_id: string | null;
  /** 按原币分组（不归一）；无充值 → [] */
  by_currency: FinanceCurrencyIncomeRow[];
};

// ─────────────────────────── ADM-FIN-S002 退款统计（原币分币种）───────────────────────────

/** 单币种退款聚合行（ADM-FIN-S002，per `paid_currency`）。 */
export type FinanceCurrencyRefundRow = {
  currency: string;
  refund_users: number;
  refund_count: number;
  total_refund_minor_units: string;
};

export type FinanceRefundStatsResponse = {
  scope: FinanceStatsScope;
  app_id: string | null;
  /**
   * 被动退款占**全部退款**比例（`"1"`/`"0"`，顶层、与币种无关）：本表 REFUND 全为 vendor 渠道被动退款、
   * 无 admin 主动退款写路径 → 有退款 = "1" / 无 = "0"。**≠** S005 的 `passive_refund_rate`（退款率）。
   */
  passive_refund_share: string;
  by_currency: FinanceCurrencyRefundRow[];
};

/** 退款率逐日趋势点（ADM-FIN 退款率趋势图）：按 occurred_at 分日聚合 REFUND/PURCHASE 笔数 → rate=refund/charge */
export type FinanceRefundTrendPoint = {
  /** 日期 YYYY-MM-DD（occurred_at date_trunc day）*/
  date: string;
  refund_count: number;
  charge_count: number;
  /** refund_count / charge_count（0~1，charge=0 时 0）*/
  refund_rate: number;
};
export type FinanceRefundTrendResponse = {
  scope: FinanceStatsScope;
  app_id: string | null;
  points: FinanceRefundTrendPoint[];
};

// ─────────────────────────── ADM-FIN-S003 退款记录 ───────────────────────────

export type RefundRecordResponse = {
  environment: 'Production' | 'Sandbox' | 'Unknown';
  id: string;
  /**
   * 支付商交易号（`purchase_amounts.vendor_transaction_id`，App Store / Stripe 原始交易 ID）。运营识别的
   * 「订单号」事实源——区别于内部行 UUID `id`。NOT NULL（镜像表约束），退款行恒有对应原交易号。
   */
  vendor_transaction_id: string;
  user_id: string;
  app_id: string | null;
  /** 原币退款额 minor units（`paid_minor_units`，REFUND 行写正数）*/
  amount_minor_units: string;
  /** 原币 ISO 4217（`paid_currency`；多币种必须随金额带币种）*/
  currency: string;
  refunded_at: string;
  /** 本表无中间态 → 恒 COMPLETED */
  final_result: 'COMPLETED' | 'PENDING' | 'REJECTED';
  /** 本表 REFUND 全为 vendor 被动退款 → 恒 PASSIVE */
  trigger: 'PASSIVE' | 'ACTIVE';
};

/**
 * 退款单运营备注（ADM-FIN-S003 详情抽屉）— 源 admin 独有表 refund_admin_notes（一退款一行）。
 * 运营内部批注，C 端不消费；与「退款原因」（vendor 侧 reason，DTO 不提供，SCHEMA_BLOCKED）无关。
 * 未录入 → note='' + updated_by/updated_at 为 null。
 */
export type RefundNoteResponse = {
  refund_id: string;
  note: string;
  updated_by: string | null;
  updated_at: string | null;
};

/** 改退款运营备注（PATCH /finance/refunds/:id/note）。note 最长 2000 字；空串=清空。 */
export const UpdateRefundNoteRequestSchema = z.object({
  note: z.string().max(2000),
});
export type UpdateRefundNoteRequest = z.infer<typeof UpdateRefundNoteRequestSchema>;

// ─────────────────────────── ADM-FIN-S005 分维度收入（原币分币种）───────────────────────────

/** 维度类型：platform（iOS/Android/WEB/OTHER）/ channel（IAP/STRIPE/已登记框架通道），映射见 A1 §5 */
export type FinanceDimensionType = 'platform' | 'channel';

/**
 * 分维度收入单行（ADM-FIN-S005，平台 / 渠道通用，在某 `currency` 组内）。
 * - dimension_key：platform `iOS|Android|WEB|OTHER`（原生 APP_STORE→iOS / PLAY_STORE→Android / 其余→OTHER；Web 财务事实→WEB）；
 *   channel `IAP|STRIPE|<框架通道代码>`（APP_STORE/PLAY_STORE→IAP / STRIPE→STRIPE / 框架通道→该通道代码，含 Web 两源）
 * - revenue_share_ratio：本维度收入 ÷ **该币种组**收入合计（0~1，4 位小数 string）
 * - passive_refund_rate：**退款率** = 维度内 REFUND 笔数 ÷ PURCHASE 笔数（0~1 string，本币种组内计数）。
 *   **≠** S002 的 `passive_refund_share`（占比）。
 */
export type FinanceDimensionRow = {
  dimension_key: string;
  charge_orders: number;
  charge_users: number;
  total_revenue_minor_units: string;
  revenue_share_ratio: string;
  passive_refund_rate: string;
};

/** 某原币组的分维度统计（含该币种维度收入合计，便于前端校验 share 归一）。 */
export type FinanceCurrencyDimensionGroup = {
  currency: string;
  total_revenue_minor_units: string;
  rows: FinanceDimensionRow[];
};

export type FinanceDimensionStatsResponse = {
  scope: FinanceStatsScope;
  app_id: string | null;
  dimension: FinanceDimensionType;
  by_currency: FinanceCurrencyDimensionGroup[];
};

// ─────────────────────────── ADM-FIN-S006 产品销量（原币分币种）───────────────────────────

/**
 * 产品销量单行（ADM-FIN-S006，在某 `currency` 组内；**仅 kind=PURCHASE**，退款不冲减）。
 * - product_id：`vendor_product_id`（归并键；同 vendor product 跨 App 汇总）
 * - kind：产品类型，event_type 推导（subscription_started/renewed/trial_converted→SUBSCRIPTION /
 *   non_subscription_purchase→CHARGE）
 * - revenue_share_ratio：本产品 ÷ 该币种组收入合计
 */
export type FinanceProductSalesRow = {
  product_id: string;
  kind: 'CHARGE' | 'SUBSCRIPTION';
  orders: number;
  users: number;
  total_revenue_minor_units: string;
  avg_order_value_minor_units: string;
  revenue_share_ratio: string;
};

export type FinanceCurrencyProductGroup = {
  currency: string;
  total_revenue_minor_units: string;
  rows: FinanceProductSalesRow[];
};

export type FinanceProductSalesResponse = {
  scope: FinanceStatsScope;
  app_id: string | null;
  by_currency: FinanceCurrencyProductGroup[];
};

// ─────────────────────────── ADM-FIN-S009 · 交易人工归属（A1 §10 / §10.0）───────────────────────────

/**
 * adapty_transaction_imports.attribution_status 归属状态闭集（dev-docs 60-订阅 §2.1）：
 * - PENDING_ATTRIBUTION：待归属（默认）
 * - ATTRIBUTED：C 端自动归属完成
 * - PERMANENT_UNATTRIBUTED：满 30 天仍不可归属 → 终态 + DLQ 告警 → 转人工归属
 * - MANUALLY_ATTRIBUTED：运营人工归属完成，重入重放
 */
export const TRANSACTION_IMPORT_ATTRIBUTION_STATUSES = [
  'PENDING_ATTRIBUTION',
  'ATTRIBUTED',
  'PERMANENT_UNATTRIBUTED',
  'MANUALLY_ATTRIBUTED',
] as const;
export type TransactionImportAttributionStatus =
  (typeof TRANSACTION_IMPORT_ATTRIBUTION_STATUSES)[number];

/**
 * 人工归属写请求（POST /finance/transaction-imports/attribution，A1 §10.1）。
 * (app_id, transaction_id) 复合定位 import 行；resolved_user_id = 人工确认归属 user；
 * resolution_note = 归属依据摘要（审计必填，≤ 2000 字符，空串/缺失 → VALIDATION_FAILED）。
 */
export const TransactionImportAttributionRequestSchema = z.object({
  app_id: z.string().uuid(),
  transaction_id: z.string().min(1).max(128),
  resolved_user_id: z.string().uuid(),
  // 审计必填：拒纯空白（min(1) 放行单空格 → audit 记空理由，对齐 sibling GrantBonus reason 的 trim 守卫）
  resolution_note: z
    .string()
    .min(1)
    .max(2000)
    .refine((s) => s.trim().length > 0, { message: 'resolution_note must not be blank' }),
});
export type TransactionImportAttributionRequest = z.infer<
  typeof TransactionImportAttributionRequestSchema
>;

/**
 * 人工归属响应（A1 §10.3）：归属成立后行进入 MANUALLY_ATTRIBUTED、随即满足重放候选条件
 * （下一轮 reconcile 自动投影，写端点不同步触发投影）。
 */
export type TransactionImportAttributionResponse = {
  attribution_status: 'MANUALLY_ATTRIBUTED';
  resolved_user_id: string;
  replay_eligible: true;
};

/**
 * 待归属队列 query（GET /finance/transaction-imports，A1 §10.0，viewer+）。
 * limit 默认 50、硬上限 200（P1-api-contract §7）；attribution_status 默认 PERMANENT_UNATTRIBUTED（DLQ 交接口径）。
 */
export const TransactionImportQueueQuerySchema = z.object({
  attribution_status: z
    .enum(TRANSACTION_IMPORT_ATTRIBUTION_STATUSES)
    .default('PERMANENT_UNATTRIBUTED'),
  /**
   * 服务端按**读侧作用域**展开为兄弟行集合，不是等值（A1 §10.0）。
   * `adapty_transaction_imports.app_id` 是「该 export 属于哪个 Adapty app」的导入入口锚、不是归属，
   * 而运营手里通常是归属 `app_id` —— 等值匹配在兄弟行拓扑下查不到自己要找的那条。
   */
  app_id: z.string().uuid().optional(),
  /**
   * 按 lineage 过滤（`adapty_transaction_imports.original_transaction_id` 等值，A1 §10.0）。
   * `permanent_ambiguous_lineage` 台账行的交接入口：台账侧无 `transaction_id` 列，运营以本参数
   * 定位队列行，再取行内 `(app_id, transaction_id)` 调 §10.2 写端点。
   */
  original_transaction_id: z.string().min(1).max(255).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type TransactionImportQueueQuery = z.infer<typeof TransactionImportQueueQuerySchema>;

/**
 * 逐条退款事实摘要（源 refund_raws 条目非 PII 字段；运营对照用户收据核验）。
 * amount_local = 原币本币额字符串（raw revenue_local，**非** minor units——金融 minor-units 解析单源 C 端
 * parsePriceLocal，admin 不复刻，dev-docs 60-订阅 §8 line 1095）。
 */
export type TransactionImportRefundEntry = {
  occurred_at: string;
  amount_local: string;
  currency: string | null;
  suspected_resend: boolean;
};

/**
 * 待归属队列行（A1 §10.0）。字段映射源 adapty_transaction_imports（dev-docs 60-订阅 §2.1）。
 *
 * 金额口径（**admin 不复刻 parsePriceLocal**，§8 line 1095 金融解析单源 C 端）：
 * - base_amount_local + base_currency = 购买侧原币本币额快照（base_price_raw.revenue_local + currency），
 *   raw local 字符串（如 "9.99"），**非 minor units**——运营对照收据本币额核验。
 * - refund_raw_count = refund_raws 全部条目数（含 suspected_resend，证据口径）；
 *   refund_candidate_count = 非 suspected 条目数（投影候选口径，文件重发不放大）。
 * 投影状态列（entitlement/credit/refund/finance_purchase/finance_refund）供前端判定改绑是否可行。
 */
export type TransactionImportQueueRow = {
  app_id: string;
  transaction_id: string;
  original_transaction_id: string;
  base_kind: 'PURCHASE' | 'RENEWAL' | 'NON_SUB_PURCHASE' | null;
  store: string;
  vendor_product_id: string;
  occurred_at: string;
  attribution_status: TransactionImportAttributionStatus;
  customer_user_id: string | null;
  resolved_user_id: string | null;
  base_amount_local: string | null;
  base_currency: string | null;
  refund_raw_count: number;
  refund_candidate_count: number;
  refund_currency: string | null;
  refund_entries: TransactionImportRefundEntry[];
  entitlement_status: 'NOT_APPLICABLE' | 'PENDING' | 'PROJECTED';
  credit_effect_status: 'PENDING' | 'EXECUTED' | 'NOT_APPLICABLE' | null;
  refund_effect_status: 'PENDING' | 'EXECUTED' | null;
  /** `NOT_APPLICABLE` = 适用性终态（A1 §10.0；成因见 `03-数据字典/60-订阅.md` §1.2.1 B 档），C 端写入 */
  finance_purchase_status: 'PENDING' | 'EXECUTED' | 'NOT_APPLICABLE' | null;
  finance_refund_status: 'PENDING' | 'EXECUTED' | 'NOT_APPLICABLE' | null;
};

// ─────────────────────────── ADM-FIN-S010 webhook 事件人工闭环（A1 §11）───────────────────────────

export const ADAPTY_EVENT_REVIEW_RESOLUTION_STATUSES = ['OPEN', 'ACKNOWLEDGED'] as const;
export type AdaptyEventReviewResolutionStatus =
  (typeof ADAPTY_EVENT_REVIEW_RESOLUTION_STATUSES)[number];

export const ADAPTY_EVENT_REVIEW_ADMISSION_KINDS = ['TERMINAL', 'RECOVERABLE_AGED_OUT'] as const;
export type AdaptyEventReviewAdmissionKind = (typeof ADAPTY_EVENT_REVIEW_ADMISSION_KINDS)[number];

/** §2.7 准入闭集六码；非法值 → 422 VALIDATION_FAILED */
export const ADAPTY_EVENT_REVIEW_ERROR_CODES = [
  'permanent_invalid_user',
  'permanent_unattributed',
  'permanent_continuity_field_missing',
  'permanent_ambiguous_lineage',
  'user_adapty_link_missing',
  'product_metadata_missing',
] as const;
export type AdaptyEventReviewErrorCode = (typeof ADAPTY_EVENT_REVIEW_ERROR_CODES)[number];

/**
 * 待办队列 query（GET /finance/adapty-event-reviews，A1 §11.0，viewer+）。
 * resolution_status 默认 OPEN；app_id 服务端按读侧作用域展开，不是等值。
 */
export const AdaptyEventReviewQueueQuerySchema = z.object({
  resolution_status: z.enum(ADAPTY_EVENT_REVIEW_RESOLUTION_STATUSES).default('OPEN'),
  admission_kind: z.enum(ADAPTY_EVENT_REVIEW_ADMISSION_KINDS).optional(),
  error_code: z.enum(ADAPTY_EVENT_REVIEW_ERROR_CODES).optional(),
  app_id: z.string().uuid().optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type AdaptyEventReviewQueueQuery = z.infer<typeof AdaptyEventReviewQueueQuerySchema>;

/**
 * 人工确认写请求（POST /finance/adapty-event-reviews/acknowledge，A1 §11.1）。
 * (app_id, adapty_event_id) 按 PK 等值命中（不走读侧作用域）；resolution_note 拒空串。
 */
export const AcknowledgeAdaptyEventReviewRequestSchema = z.object({
  app_id: z.string().uuid(),
  adapty_event_id: z.string().min(1).max(128),
  resolution_note: z
    .string()
    .min(1)
    .max(2000)
    .refine((s) => s.trim().length > 0, { message: 'resolution_note must not be blank' }),
});
export type AcknowledgeAdaptyEventReviewRequest = z.infer<
  typeof AcknowledgeAdaptyEventReviewRequestSchema
>;

/** 人工确认响应（A1 §11.3）；no-op 分支返回首次确认的 acknowledged_at */
export type AcknowledgeAdaptyEventReviewResponse = {
  resolution_status: 'ACKNOWLEDGED';
  acknowledged_at: string;
};

/**
 * 待办队列行（A1 §11.0）。逐列直取 adapty_event_manual_reviews 行内快照，
 * 不反查源表 / R2，不透传 payload。
 */
export type AdaptyEventReviewQueueRow = {
  app_id: string;
  adapty_event_id: string;
  event_type: string;
  admission_kind: AdaptyEventReviewAdmissionKind;
  error_code: string;
  event_received_at: string;
  admitted_at: string;
  adapty_profile_id: string | null;
  customer_user_id: string | null;
  original_transaction_id: string | null;
  store: string | null;
  vendor_product_id: string | null;
  missing_fields: string[] | null;
  alert_ref: string | null;
  resolution_status: AdaptyEventReviewResolutionStatus;
  acknowledged_at: string | null;
  resolution_note: string | null;
};
